import React, { useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "@/assets/icons/close.svg";
import "./GenericModal.scss";

type Props = {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
};

export const GenericModal: React.FC<Props> = ({
  isOpen,
  title,
  onClose,
  children,
  className,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [isOpen]);

  const modalContent = useMemo(() => {
    if (!isOpen) return null;
    return (
      <div className="modal-overlay" role="presentation" onClick={onClose}>
        <div
          className={`modal ${className ?? ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <div className="modal-title">{title}</div>
            <button
              type="button"
              className="modal-close-button"
              onClick={onClose}
              aria-label={t("buttons.close")}
            >
              <CloseIcon aria-hidden="true" focusable="false" />
            </button>
          </div>
          <div className="modal-content">{children}</div>
        </div>
      </div>
    );
  }, [className, children, isOpen, onClose, t, title]);

  return ReactDOM.createPortal(modalContent, document.body);
};
